import { useState, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import LTVMainContent from './LTVMainContent';
import LoadingAnimation from '../../../Animation/NewLoading.json';

export default function DurationBox(props) {
  const {
    calcDateText,
    startDate,
    endDate,
    revenue,
    durationAvg,
    durationMembers,
    allMembers,
    isOpenPartnerGymSales,
    isDurationLoading
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  // 월평균 매출 계산: revenue(sales/revenue/year) 데이터 YTD 기준 //
  // 과거 연도: 1월 ~ 12월 (전체) / 올해: 1월 ~ 현재 달까지 //
  const priceAvg = useMemo(() => {
    if (!revenue || revenue.length === 0 || !startDate) return 0;

    const now = moment();
    const targetYear = startDate.year();
    const lastMonth = targetYear === now.year() ? now.month() + 1 : 12;

    let totalSum = 0;

    for (let i = 0; i < revenue.length; i++) {
      const sale = revenue[i];

      if (!sale.approvalDate) continue;

      const year = parseInt(sale.approvalDate.slice(0, 4));
      const month = parseInt(sale.approvalDate.slice(5, 7));

      if (year === targetYear && month >= 1 && month <= lastMonth) {
        totalSum += sale.totalPrice;
      }
    }

    return Math.round(totalSum / lastMonth);
  }, [revenue, startDate]);

  // 산정기간 회원권 overlap 회원만 포함하는 유효 회원 목록 //
  const filteredDurationMembers = useMemo(() => {
    if (!startDate || !endDate || !Array.isArray(allMembers) || allMembers.length === 0) return durationMembers;

    const startTs = moment(startDate).startOf('day').valueOf();
    const endTs = moment(endDate).endOf('day').valueOf();

    const validMemberIds = new Set(
      allMembers
        .filter(m => Array.isArray(m.usages) && m.usages.some(u =>
          moment(u.startDate).startOf('day').valueOf() <= endTs
          && moment(u.endDate).startOf('day').valueOf() >= startTs
        ))
        .map(m => m.memberId)
    );

    return durationMembers.filter(d => validMemberIds.has(d.memberId));
  }, [durationMembers, allMembers, startDate, endDate]);

  const [ltvData, setLtvData] = useState({
    revenueAvgPerMember: 0,
    maxAdSpend: 0,
    totalRevenue: 0,
  });

  const { revenueAvgPerMember, maxAdSpend, totalRevenue } = ltvData;

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isDurationLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          isOpenPartnerGymSales ?
            <ContentWrapper>
              {/* 타이틀 */}
              <TitleRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {calcDateText() + ' LIFETIME VALUE'}
                </Text>
              </TitleRow>
              <SubtitleRow>
                <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
                  {t('statistics.duration.subtitle')}
                </Text>
              </SubtitleRow>

              {/* 메인 콘텐츠: 왼쪽(LTV) + 오른쪽(슬라이더) */}
              <LTVMainContent
                priceAvg={priceAvg}
                durationAvg={durationAvg}
                durationMembers={filteredDurationMembers}
                startDate={startDate}
                onChange={setLtvData}
              />

              {/* 하단 3개 카드 */}
              <BottomCardRow>
                <BottomCard backgroundColor={theme.buttonFill}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.recommendedMaxAdSpend')}
                  </Text>
                  <BottomCardValueRow>
                    {
                      t('statistics.duration.currencyPrefix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencyPrefix')}
                      </Text>
                    }
                    <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.subKey}>
                      {maxAdSpend.toLocaleString('ko-KR')}
                    </Text>
                    {
                      t('statistics.duration.currencySuffix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencySuffix')}
                      </Text>
                    }
                  </BottomCardValueRow>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.keepWithin30')}
                  </Text>
                </BottomCard>

                <BottomCard backgroundColor={theme.buttonFill}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.revenueAvgPerMemberMonth')}
                  </Text>
                  <BottomCardValueRow>
                    {
                      t('statistics.duration.currencyPrefix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencyPrefix')}
                      </Text>
                    }
                    <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.subKey}>
                      {revenueAvgPerMember.toLocaleString('ko-KR')}
                    </Text>
                    {
                      t('statistics.duration.currencySuffix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencySuffix')}
                      </Text>
                    }
                  </BottomCardValueRow>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.monthlyValuePerMember')}
                  </Text>
                </BottomCard>

                <BottomCard backgroundColor={theme.buttonFill}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.totalRevenueAllMembers')}
                  </Text>
                  <BottomCardValueRow>
                    {
                      t('statistics.duration.currencyPrefix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencyPrefix')}
                      </Text>
                    }
                    <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.subKey}>
                      {totalRevenue.toLocaleString('ko-KR')}
                    </Text>
                    {
                      t('statistics.duration.currencySuffix') &&
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>
                        {t('statistics.duration.currencySuffix')}
                      </Text>
                    }
                  </BottomCardValueRow>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.duration.ltvTimesAllMembers')}
                  </Text>
                </BottomCard>
              </BottomCardRow>
            </ContentWrapper>
            :
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  margin: 38px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;

  gap: 10px;

  border-radius: 20px;
  box-shadow: ${props => props.$isDark ? 'none' : '0 40px 50px rgba(240, 232, 227, 0.5)'};
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  width: 100%;

  gap: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const BottomCardRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;
  gap: 20px;
`;

const BottomCard = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  justify-content: center;
  align-items: flex-start;

  padding: 10px 20px;

  gap: 5px;

  border-radius: 8px;
  background-color: ${props => props.backgroundColor};

  overflow: hidden;
  white-space: nowrap;
`;

const BottomCardValueRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 5px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  padding: 40px 0;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;